import React, { type KeyboardEvent, useCallback, memo, useEffect } from 'react';
import { styled } from '@compiled/react';
import { useBulkOpsSessionContext } from '@atlassian/jira-bulk-operations-analytics';
import { OPTIMISTIC_ID_PREFIX } from '@atlassian/jira-business-list-handlers';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	BULK_SELECT_CHECKBOX_ID,
	DRAG_AND_DROP_BUTTON_FOCUS_EVENT,
} from '../../../common/constants';
import type { CellRendererProps } from '../../../common/types/base-table';
import type { CellNavigationProps } from '../../../common/types/cell-navigation';
import { BulkSelectCheckbox } from '../../../common/ui/bulk-select-checkbox';
import {
	useBulkSelectionStore,
	useIsIssueSelected,
	useSelectAllCheckbox,
	useShiftKeySelection,
} from '../../../controllers/bulk-selection';
import messages from './messages';

type Props = CellRendererProps & CellNavigationProps;

export const SelectRowCheckbox = memo<Props>(({ rowData, rowIndex, onKeyDown, ...props }) => {
	const { issueId: rowIssueId, id } = rowData;
	const issueId = String(rowIssueId);

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, { selectIssues, unselectIssues }] = useBulkSelectionStore();
	const [isRowSelected] = useIsIssueSelected({
		issueId,
		parentIssueId: rowData.parentIssueId,
	});
	const { onShiftKeyBulkSelection } = useShiftKeySelection();
	const bulkOpsFlow = useBulkOpsSessionContext();

	const onClickCheckbox = useCallback(
		({ isChecked, shiftKeyPressed }: { isChecked: boolean; shiftKeyPressed: boolean }) => {
			const bulkOpsSessionID = bulkOpsFlow?.getFlowAttributes()?.bulkOpsSessionID ?? '';
			fireUIAnalytics(createAnalyticsEvent({}), 'checkbox clicked', 'selectIssue', {
				isChecked,
				rowIndex,
				shiftKeyPressed,
				bulkOpsSessionID,
			});
			if (isChecked) {
				selectIssues({ issues: [issueId], rowIndex });
			} else {
				unselectIssues({ issues: [issueId], rowIndex });
			}

			if (shiftKeyPressed) {
				onShiftKeyBulkSelection({
					isChecked,
					rowIndex,
				});
			}
		},
		[
			createAnalyticsEvent,
			issueId,
			onShiftKeyBulkSelection,
			rowIndex,
			selectIssues,
			unselectIssues,
			bulkOpsFlow,
		],
	);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLDivElement>) => {
			if (e.key === 'Enter') {
				onClickCheckbox({ isChecked: !isRowSelected, shiftKeyPressed: e.shiftKey });
				return;
			}

			if ((e.shiftKey && e.key === 'Tab') || e.key === 'ArrowLeft') {
				e.preventDefault();
				document.dispatchEvent(
					new CustomEvent(`${DRAG_AND_DROP_BUTTON_FOCUS_EVENT}`, {
						detail: { issueId },
					}),
				);
				return;
			}

			onKeyDown?.(e);
		},
		[isRowSelected, onClickCheckbox, onKeyDown, issueId],
	);

	useEffect(() => {
		const focusOnCheckbox = (e: CustomEventInit) => {
			if (String(e.detail.issueId) !== issueId) {
				return;
			}

			const checkbox = document.getElementById(`${BULK_SELECT_CHECKBOX_ID}-${issueId}`);
			checkbox?.focus();
		};

		document.addEventListener(`${BULK_SELECT_CHECKBOX_ID}-focus`, focusOnCheckbox);

		return () => {
			document.removeEventListener(`${BULK_SELECT_CHECKBOX_ID}-focus`, focusOnCheckbox);
		};
	}, [issueId]);

	return (
		<SingleSelectChecboxContainer>
			<BulkSelectCheckbox
				id={`${BULK_SELECT_CHECKBOX_ID}-${issueId}`}
				onClick={onClickCheckbox}
				isChecked={isRowSelected}
				onKeyDown={handleKeyDown}
				isDisabled={id.startsWith(OPTIMISTIC_ID_PREFIX)}
				label={formatMessage(messages.selectIssue, { issueKey: rowData.key })}
				{...props}
			/>
		</SingleSelectChecboxContainer>
	);
});

export const SelectAllColumnCheckbox = memo(() => {
	const { isIndeterminate, onClickCheckbox, isChecked } = useSelectAllCheckbox();

	const { formatMessage } = useIntl();

	return (
		<BulkSelectCheckbox
			onClick={onClickCheckbox}
			isChecked={isChecked || isIndeterminate}
			isIndeterminate={isIndeterminate}
			label={formatMessage(messages.selectAll)}
		/>
	);
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SingleSelectChecboxContainer = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '19px',
});
